import {
  faChevronLeft,
  faChevronRight,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';

import IconButton from 'atoms/buttons/IconButton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Dialog } from 'components/dialog';
import useTransitionApi from 'hooks/ui/useTransitionApi';

import { DurationsIndicator } from './DurationsIndicator';

const Root = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;
const Content = styled(Horizontal)`
  isolation: isolate;
  height: 100%;
`;
const Header = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  position: absolute;
  top: 0;
  z-index: 1;
`;
const TitleAndCloseButton = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 var(--double-unit);
`;
const StoryTitle = styled(Vertical)`
  flex: 1;
  padding-left: 40px;
`;
const BackButtonWrapper = styled.div`
  position: absolute;
  left: var(--double-unit);
`;
const NextButtonWrapper = styled.div`
  position: absolute;
  right: var(--double-unit);
`;

export type Story = {
  onEnd?: () => void;
  duration: number;
  bodyElement: ReactNode;
  titleElement?: ReactNode;
};
type Props = {
  onClose: () => void;
  stories: Story[];
  pause?: boolean;
  handleOnPause: (e: React.MouseEvent | React.TouchEvent) => void;
  handleOnUnpause: (e: React.MouseEvent | React.TouchEvent) => void;
  goBackToBeginning?: boolean;
};
export const Story = ({
  onClose: onCloseProp,
  stories,
  pause = false,
  handleOnPause,
  handleOnUnpause,
  goBackToBeginning = true,
}: Props) => {
  const { updateDOM } = useTransitionApi();
  const [currentlyPlayingStoryIndex, setCurrentlyPlayingStoryIndex] =
    useState(0);

  const onBack = () => {
    setCurrentlyPlayingStoryIndex(index => (index > 0 ? index - 1 : index));
  };
  const onNext = () => {
    stories[currentlyPlayingStoryIndex]?.onEnd?.();
    updateDOM(() => {
      setCurrentlyPlayingStoryIndex(index => (index + 1) % stories.length);
    });
  };
  const onClose = () => {
    stories[currentlyPlayingStoryIndex]?.onEnd?.();
    onCloseProp();
  };

  const onStoryEnd = useCallback(() => {
    stories[currentlyPlayingStoryIndex]?.onEnd?.();
    if (currentlyPlayingStoryIndex === stories.length - 1) {
      onCloseProp();
      if (goBackToBeginning) {
        setTimeout(() => {
          setCurrentlyPlayingStoryIndex(0);
        }, 1000);
      }
    } else {
      setCurrentlyPlayingStoryIndex(index => index + 1);
    }
  }, [currentlyPlayingStoryIndex, onCloseProp, stories, goBackToBeginning]);

  if (!stories.length) {
    return null;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" hideHeader>
      <Root
        onMouseDown={handleOnPause}
        onTouchStart={handleOnPause}
        onTouchEnd={handleOnUnpause}
        onMouseUp={handleOnUnpause}
      >
        <Header>
          <DurationsIndicator
            steps={stories.map(({ duration }) => duration)}
            playing
            currentlyPlayingStoryIndex={currentlyPlayingStoryIndex}
            onStoryEnd={onStoryEnd}
            pause={pause}
          />
          <TitleAndCloseButton>
            <StoryTitle center>
              {stories.at(currentlyPlayingStoryIndex)?.titleElement}
            </StoryTitle>
            <IconButton onClick={onClose} icon={faTimes} color="transparent" />
          </TitleAndCloseButton>
        </Header>
        <Content>
          {stories.at(currentlyPlayingStoryIndex)?.bodyElement}
          {currentlyPlayingStoryIndex > 0 && (
            <BackButtonWrapper>
              <IconButton
                icon={faChevronLeft}
                onClick={onBack}
                disableDebounce
                color="transparent"
              />
            </BackButtonWrapper>
          )}
          {currentlyPlayingStoryIndex < stories.length - 1 && (
            <NextButtonWrapper>
              <IconButton
                icon={faChevronRight}
                onClick={onNext}
                color="transparent"
              />
            </NextButtonWrapper>
          )}
        </Content>
      </Root>
    </Dialog>
  );
};
